import { useEffect, useCallback } from 'react';
import MixpanelAnalytics, { ANALYTICS_EVENTS } from '../analytics/mixpanel';

// Custom hook for analytics tracking
export const useAnalytics = () => {
  const trackEvent = useCallback((eventName, properties = {}) => {
    MixpanelAnalytics.track(eventName, properties);
  }, []);

  const trackPageView = useCallback((pageName, properties = {}) => {
    MixpanelAnalytics.pageView(pageName, properties);
  }, []);

  const identifyUser = useCallback((userId, properties = {}) => {
    MixpanelAnalytics.identify(userId, properties);
  }, []);

  const trackError = useCallback((errorMessage, errorContext = {}) => {
    MixpanelAnalytics.trackError(errorMessage, errorContext);
  }, []);

  const updateUserProperties = useCallback((properties) => {
    MixpanelAnalytics.updateUserProperties(properties);
  }, []);

  return {
    trackEvent,
    trackPageView,
    identifyUser,
    trackError,
    updateUserProperties,
    EVENTS: ANALYTICS_EVENTS
  };
};

// Hook for automatic page tracking
export const usePageTracking = (pageName) => {
  const { trackPageView } = useAnalytics();

  useEffect(() => {
    if (pageName) {
      trackPageView(pageName, {
        path: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
      });
    }
  }, [pageName, trackPageView]);
};

// Hook for tracking tab switches in Dashboard
export const useTabTracking = (activeTab) => {
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    if (activeTab) {
      trackEvent(ANALYTICS_EVENTS.TAB_SWITCHED, {
        tab_name: activeTab,
        tab_category: 'dashboard'
      });
    }
  }, [activeTab, trackEvent]);
};

// Hook for tracking form interactions
export const useFormAnalytics = (formName) => {
  const { trackEvent, trackError } = useAnalytics();

  const trackFormStart = useCallback((formData = {}) => {
    trackEvent('Form Started', {
      form_name: formName,
      ...formData
    });
  }, [formName, trackEvent]);

  const trackFormSubmit = useCallback((formData = {}) => {
    trackEvent('Form Submitted', {
      form_name: formName,
      ...formData
    });
  }, [formName, trackEvent]);

  const trackFormError = useCallback((errorMessage, formData = {}) => {
    trackError(errorMessage, {
      component: 'Form',
      form_name: formName,
      action: 'form_submit',
      ...formData
    });
  }, [formName, trackError]);

  const trackFieldInteraction = useCallback((fieldName, action = 'focus') => {
    trackEvent('Form Field Interaction', {
      form_name: formName,
      field_name: fieldName,
      action: action
    });
  }, [formName, trackEvent]);

  return {
    trackFormStart,
    trackFormSubmit,
    trackFormError,
    trackFieldInteraction
  };
};

// Hook for tracking search and filter interactions
export const useSearchAnalytics = () => {
  const { trackEvent } = useAnalytics();

  const trackSearch = useCallback((query, searchType, results = {}) => {
    trackEvent(ANALYTICS_EVENTS.CONTENT_SEARCHED, {
      search_query: query,
      search_type: searchType,
      results_count: results.count,
      has_results: (results.count || 0) > 0,
      search_category: results.category || 'all'
    });
  }, [trackEvent]);

  const trackMeetupSearch = useCallback((query, filters = {}) => {
    trackEvent(ANALYTICS_EVENTS.MEETUP_SEARCHED, {
      search_query: query,
      filters_applied: Object.keys(filters).length,
      category_filter: filters.category,
      tag_filter: filters.tag,
      status_filter: filters.status
    });
  }, [trackEvent]);

  const trackFilter = useCallback((filterType, filterValue, context = 'content') => {
    trackEvent(context === 'meetup' ? ANALYTICS_EVENTS.MEETUP_FILTERED : ANALYTICS_EVENTS.SEARCH_FILTER_USED, {
      filter_type: filterType,
      filter_value: filterValue,
      context: context
    });
  }, [trackEvent]);

  return {
    trackSearch,
    trackMeetupSearch,
    trackFilter
  };
};

// Hook for tracking meetup interactions
export const useMeetupAnalytics = () => {
  const { trackEvent } = useAnalytics();

  const trackMeetupCreate = useCallback((meetupData) => {
    trackEvent(ANALYTICS_EVENTS.MEETUP_CREATED, {
      meetup_title: meetupData.title,
      meetup_categories: meetupData.categories?.length || 0,
      meetup_tags: meetupData.tags?.length || 0,
      max_participants: meetupData.max_participants,
      has_location: !!meetupData.location
    });
  }, [trackEvent]);

  const trackMeetupJoin = useCallback((meetupId, meetupTitle) => {
    trackEvent(ANALYTICS_EVENTS.MEETUP_JOINED, {
      meetup_id: meetupId,
      meetup_title: meetupTitle
    });
  }, [trackEvent]);

  const trackMeetupLeave = useCallback((meetupId, meetupTitle) => {
    trackEvent(ANALYTICS_EVENTS.MEETUP_LEFT, {
      meetup_id: meetupId,
      meetup_title: meetupTitle
    });
  }, [trackEvent]);

  const trackMeetupView = useCallback((meetupData, viewType = 'detail') => {
    trackEvent(ANALYTICS_EVENTS.MEETUP_VIEWED, {
      meetup_id: meetupData.id,
      meetup_title: meetupData.title,
      view_type: viewType,
      participant_count: meetupData.participant_count,
      is_creator: meetupData.is_creator,
      is_participant: meetupData.is_participant
    });
  }, [trackEvent]);

  return {
    trackMeetupCreate,
    trackMeetupJoin,
    trackMeetupLeave,
    trackMeetupView
  };
};

// Hook for tracking content interactions
export const useContentAnalytics = () => {
  const { trackEvent } = useAnalytics();

  const trackContentView = useCallback((content, viewType = 'card') => {
    trackEvent(ANALYTICS_EVENTS.CONTENT_VIEWED, {
      content_id: content.id,
      content_title: content.title || content.name,
      content_type: content.media_type || 'unknown',
      view_type: viewType,
      has_streaming: !!content.streaming_availability
    });
  }, [trackEvent]);

  const trackFavorite = useCallback((content, action = 'add') => {
    trackEvent(action === 'add' ? ANALYTICS_EVENTS.CONTENT_FAVORITED : ANALYTICS_EVENTS.CONTENT_UNFAVORITED, {
      content_id: content.id,
      content_title: content.title || content.name,
      content_type: content.media_type || 'unknown'
    });
  }, [trackEvent]);

  const trackShare = useCallback((content) => {
    trackEvent(ANALYTICS_EVENTS.CONTENT_SHARED, {
      content_id: content.id,
      content_title: content.title || content.name,
      content_type: content.media_type || 'unknown'
    });
  }, [trackEvent]);

  return {
    trackContentView,
    trackFavorite,
    trackShare
  };
};